import React, { useState, useCallback, useRef, useEffect } from "react";
import { FiMenu, FiSearch, FiX } from "react-icons/fi";
import AccountMenu from "../DropDown/AccountMenu";
import { Link, useNavigate } from 'react-router-dom'
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  IconButton,
  Typography,
  Container,
  ClickAwayListener,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip
} from "@mui/material";
import BellIcon from "../BellIcon/BelllIcon";

const AppBarContainer = styled("header")(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  maxHeight: 64,
  display: 'flex',
  alignItems: 'center',
  zIndex: theme.zIndex.appBar,
  backgroundColor: "#ffffff",
  borderBottom: "1px solid #e5e7eb",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
  backdropFilter: "blur(8px)",
}));

const SearchContainer = styled(Box)(({ theme, isMobile }) => ({
  position: isMobile ? "static" : "absolute",
  left: isMobile ? "auto" : "45%",
  transform: isMobile ? "none" : "translateX(-50%)",
  width: "100%",
  maxWidth: isMobile ? "100%" : "520px",
  [theme.breakpoints.up('lg')]: {
    maxWidth: "600px",
  }
}));

const SearchInputWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  backgroundColor: "#f9fafb",
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  overflow: "hidden",
  transition: "all 0.2s ease-in-out",
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
    backgroundColor: "#ffffff",
  },
  "&:hover": {
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
  }
}));

const SearchInput = styled("input")(({ theme }) => ({
  display: "block",
  width: "100%",
  paddingLeft: theme.spacing(4.5),
  paddingRight: theme.spacing(1.5),
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  border: "none",
  backgroundColor: "transparent",
  color: theme.palette.text.primary,
  fontSize: "0.875rem",
  "&:focus": {
    outline: "none",
  },
  "&::placeholder": {
    color: "#9ca3af",
    fontWeight: 400,
  },
}));

const SearchResultsDropdown = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  marginTop: theme.spacing(0.5),
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  boxShadow: "0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  border: "1px solid #e5e7eb",
  overflow: "hidden",
  zIndex: theme.zIndex.modal + 1,
  maxHeight: "400px",
  overflowY: "auto",
}));

const ResponsiveAppBar = ({ logout, handleDrawerToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);

  // Mock search data - Replace with your actual data source
  const searchData = [
    { id: 1, title: "Dashboard", type: "page", path: "/dashboard", icon: "📊" },
    { id: 2, title: "Analytics", type: "page", path: "/analytics", icon: "📈" },
    { id: 3, title: "Settings", type: "page", path: "/settings", icon: "⚙️" },
    { id: 4, title: "Profile", type: "page", path: "/profile", icon: "👤" },
    { id: 5, title: "Reports", type: "page", path: "/reports", icon: "📋" },
    { id: 6, title: "Users", type: "page", path: "/users", icon: "👥" },
    { id: 7, title: "Projects", type: "page", path: "/projects", icon: "📁" },
    { id: 8, title: "Link Shortener", type: "feature", path: "/links", icon: "🔗" },
    { id: 9, title: "PDF Generator", type: "feature", path: "/pdf", icon: "📄" },
    { id: 10, title: "Screenshots", type: "feature", path: "/screenshots", icon: "📸" }
  ];

  // Debounced search function
  useEffect(() => {
    if (!searchValue.trim()) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(() => {
      performSearch(searchValue);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const performSearch = useCallback((query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    // Simulate API call delay
    setTimeout(() => {
      const results = searchData.filter(item =>
        item.title.toLowerCase().includes(query.toLowerCase()) ||
        item.type.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8); // Limit results

      setSearchResults(results);
      setShowResults(true);
      setIsSearching(false);
    }, 150);
  }, [searchData]);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchValue(value);
  }, []);

  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    if (searchValue.trim() && searchResults.length > 0) {
      handleResultClick(searchResults[0]);
    }
  }, [searchValue, searchResults]);

  const handleResultClick = useCallback((result) => {
    setSearchValue("");
    setShowResults(false);
    setMobileSearchOpen(false);
    navigate(result.path);
  }, [navigate]);

  const handleSearchFocus = useCallback(() => {
    if (searchValue.trim()) {
      setShowResults(true);
    }
  }, [searchValue]);

  const handleClickAway = useCallback(() => {
    setShowResults(false);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchValue("");
    setSearchResults([]);
    setShowResults(false);
    setMobileSearchOpen(false);
  }, []);

  const getTypeColor = (type) => {
    switch (type) {
      case 'page': return 'primary';
      case 'feature': return 'secondary';
      default: return 'default';
    }
  };

  const SearchComponent = ({ isMobile = false }) => (
    <ClickAwayListener onClickAway={handleClickAway}>
      <SearchContainer isMobile={isMobile}>
        <form onSubmit={handleSearchSubmit}>
          <SearchInputWrapper>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: 14,
                transform: "translateY(-50%)",
                color: "text.secondary",
                zIndex: 1,
              }}
            >
              <FiSearch size={16} />
            </Box>
            <SearchInput
              ref={searchInputRef}
              type="text"
              placeholder="Search pages, features, settings..."
              value={searchValue}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              autoComplete="off"
            />
            {searchValue && (
              <IconButton
                size="small"
                onClick={clearSearch}
                sx={{
                  position: "absolute",
                  right: 8,
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  }
                }}
              >
                <FiX size={14} />
              </IconButton>
            )}
          </SearchInputWrapper>
        </form>

        {/* Search Results Dropdown */}
        {showResults && (
          <SearchResultsDropdown>
            {isSearching ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Searching...
                </Typography>
              </Box>
            ) : searchResults.length > 0 ? (
              <>
                <Box sx={{ p: 2, pb: 1 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Found {searchResults.length} results for "{searchValue}"
                  </Typography>
                </Box>
                <List disablePadding>
                  {searchResults.map((result, index) => (
                    <React.Fragment key={result.id}>
                      <ListItem
                        component="button"
                        onClick={() => handleResultClick(result)}
                        sx={{
                          py: 1.5,
                          px: 2,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.04)",
                          },
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: "auto", fontSize: "1.2rem" }}>
                          {result.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={result.title}
                          secondary={result.path}
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: 500,
                          }}
                          secondaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                          }}
                        />
                        <Chip
                          label={result.type}
                          size="small"
                          color={getTypeColor(result.type)}
                          variant="outlined"
                          sx={{ fontSize: "0.75rem", height: "20px" }}
                        />
                      </ListItem>
                      {index < searchResults.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </>
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No results found for "{searchValue}"
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Try different keywords or check spelling
                </Typography>
              </Box>
            )}
          </SearchResultsDropdown>
        )}
      </SearchContainer>
    </ClickAwayListener>
  );

  return (
    <AppBarContainer>
      <Container maxWidth={false} sx={{ width: '100%', py: 1.5 }}>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: "50px",
          }}
        >
          {/* Left section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                display: { xs: "flex", md: "none" },
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                }
              }}
            >
              <FiMenu />
            </IconButton>
          </Box>

          {/* Center - Search bar for desktop */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              width: "100%",
              maxWidth: "520px",
              [theme.breakpoints.up('lg')]: {
                maxWidth: "600px",
              }
            }}
          >
            <SearchComponent />
          </Box>

          {/* Right side icons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Mobile Search */}
            <IconButton
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              sx={{
                display: { xs: "flex", md: "none" },
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                }
              }}
            >
              <FiSearch />
            </IconButton>

            {/* Notifications */}
            <Box sx={{ position: "relative" }}>
              <BellIcon />
            </Box>

            {/* User Account */}
            <AccountMenu logout={logout} />
          </Box>
        </Box>

        {/* Mobile Search Panel */}
        {mobileSearchOpen && (
          <Box mt={2} display={{ xs: "block", md: "none" }}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box sx={{ flex: 1 }}>
                <SearchComponent isMobile={true} />
              </Box>
              <IconButton
                onClick={() => setMobileSearchOpen(false)}
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  }
                }}
              >
                <FiX />
              </IconButton>
            </Box>
          </Box>
        )}
      </Container>
    </AppBarContainer>
  );
};

export default React.memo(ResponsiveAppBar);
